import React, { useState } from "react";
import {
  Cpu,
  Zap,
  HardDrive,
  Globe,
  Server,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Clock,
  Sparkles,
  ChevronDown,
  Layers,
  Lock,
  Settings,
  Headphones,
  Terminal,
  Activity,
  Check,
  Award,
} from "lucide-react";

export default function MeoHostVdsView({
  plans = [],
  openOrderModal,
  billingCycle = "monthly",
  setBillingCycle,
}) {
  const [activeFaq, setActiveFaq] = useState(null);

  // Default fallback plans if DB has fewer or empty (100% German)
  const defaultVdsPlans = [
    {
      id: "vps-mini",
      slug: "vps-mini",
      name: "Premium Mini VDS",
      displayName: "Premium Mini VDS",
      category: "vps",
      unitPrice: 19.90,
      originalPrice: 39.90,
      badge: "EINSTEIGER",
      isHighlight: false,
      specs: {
        cores: 3,
        cpuModel: "Gold 6230R",
        ram: "6 GB",
        ramType: "DDR4 ECC",
        disk: "40 GB",
        diskType: "NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
      },
    },
    {
      id: "vps-standard",
      slug: "vps-standard",
      name: "Premium Standard VDS",
      displayName: "Premium Standard VDS",
      category: "vps",
      unitPrice: 29.90,
      originalPrice: 59.90,
      badge: "POPULÄR",
      isHighlight: false,
      specs: {
        cores: 5,
        cpuModel: "Gold 6230R",
        ram: "10 GB",
        ramType: "DDR4 ECC",
        disk: "125 GB",
        diskType: "NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
      },
    },
    {
      id: "vps-power",
      slug: "vps-power",
      name: "Premium Power VDS",
      displayName: "Premium Power VDS",
      category: "vps",
      unitPrice: 49.90,
      originalPrice: 89.90,
      badge: "★ EMPFOHLEN",
      isHighlight: true,
      specs: {
        cores: 7,
        cpuModel: "Gold 6230R",
        ram: "14 GB",
        ramType: "DDR4 ECC",
        disk: "150 GB",
        diskType: "NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
      },
    },
    {
      id: "vps-ultra",
      slug: "vps-ultra",
      name: "Premium Ultra VDS",
      displayName: "Premium Ultra VDS",
      category: "vps",
      unitPrice: 79.90,
      originalPrice: 129.90,
      badge: "ENTERPRISE",
      isHighlight: false,
      specs: {
        cores: 10,
        cpuModel: "Gold 6230R",
        ram: "20 GB",
        ramType: "DDR4 ECC",
        disk: "200 GB",
        diskType: "NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
      },
    },
    {
      id: "vps-mega",
      slug: "vps-mega",
      name: "Premium Mega VDS",
      displayName: "Premium Mega VDS",
      category: "vps",
      unitPrice: 119.90,
      originalPrice: 189.90,
      badge: "HIGH PERFORMANCE",
      isHighlight: false,
      specs: {
        cores: 14,
        cpuModel: "Gold 6230R",
        ram: "28 GB",
        ramType: "DDR4 ECC",
        disk: "300 GB",
        diskType: "NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
      },
    },
    {
      id: "vps-god",
      slug: "vps-god",
      name: "Premium God VDS",
      displayName: "Premium God VDS",
      category: "vps",
      unitPrice: 169.90,
      originalPrice: 269.90,
      badge: "MAXIMAL POWER",
      isHighlight: false,
      specs: {
        cores: 20,
        cpuModel: "Gold 6230R",
        ram: "48 GB",
        ramType: "DDR4 ECC",
        disk: "350 GB",
        diskType: "NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
      },
    },
  ];

  // Merge DB plans or use defaults
  const effectivePlans = plans && plans.length > 0 ? plans : defaultVdsPlans;

  // Normalise each plan to MeoHost table columns
  const tableData = effectivePlans.map((plan, idx) => {
    const rawUnitPrice = Number(plan.unitPrice || 29.90);
    const isYearly = billingCycle === "yearly";
    const discountedPrice = isYearly ? rawUnitPrice * 0.9 : rawUnitPrice;
    const originalPrice = plan.originalPrice || Math.round(rawUnitPrice * 1.6);

    // Extract specs safely
    let cores = 3;
    let ram = "6 GB";
    let disk = "40 GB";
    let port = "1 Gbit/s";
    let traffic = "Unlimitiert";

    if (plan.specs) {
      if (typeof plan.specs === "object") {
        if (plan.specs.cores) cores = plan.specs.cores;
        else if (plan.specs.cpu) {
          const match = String(plan.specs.cpu).match(/\d+/);
          if (match) cores = parseInt(match[0], 10);
        }
        if (plan.specs.ram) ram = String(plan.specs.ram);
        if (plan.specs.disk) disk = String(plan.specs.disk);
        if (plan.specs.port) port = String(plan.specs.port);
        if (plan.specs.traffic) {
          traffic = String(plan.specs.traffic).replace(/limitsiz/i, "Unlimitiert");
        }
      }
    } else {
      const defaultMatch = defaultVdsPlans[idx] || defaultVdsPlans[0];
      cores = defaultMatch.specs.cores;
      ram = defaultMatch.specs.ram;
      disk = defaultMatch.specs.disk;
    }

    const isFeatured = Boolean(
      plan.isHighlight ||
      (plan.badge && (plan.badge.includes("EMPFOHLEN") || plan.badge.includes("BESTSELLER") || plan.badge.includes("ÖNE"))) ||
      (plan.displayName && plan.displayName.includes("Power"))
    );

    return {
      ...plan,
      displayName: (plan.displayName || plan.name || "").replace(/standart/i, "Standard"),
      badge: (plan.badge || "").replace(/öne çıkan/i, "EMPFOHLEN").replace(/giriş seviyesi/i, "EINSTEIGER"),
      cores,
      ram,
      disk,
      port,
      traffic,
      originalPrice,
      discountedPrice,
      isFeatured,
    };
  });

  const faqs = [
    {
      q: "Was ist der Unterschied zwischen einem VDS und geteiltem Webhosting?",
      a: "Bei regulärem Webhosting teilen sich mehrere Kunden die Server-Ressourcen (CPU, RAM). Bei einem VDS (Virtual Dedicated Server) werden Ihnen per KVM-Hardware-Virtualisierung feste Intel Xeon Gold Kerne, DDR4 ECC RAM und Enterprise NVMe Speicher exklusiv zugewiesen. Andere Nutzer können Ihre Performance nicht beeinträchtigen, und Sie erhalten vollen Root/SSH-Zugriff.",
    },
    {
      q: "Wie schnell ist der VDS-Server nach der Bestellung einsatzbereit?",
      a: "Dank des vollautomatisierten RedWORK Bereitstellungssystems wird Ihr VDS nach Zahlungseingang in der Regel innert 60 Sekunden aufgesetzt. IP-Adresse, Root-Passwort und Zugangsdaten werden Ihnen sofort im Kundenportal angezeigt und per E-Mail zugestellt.",
    },
    {
      q: "Welche Betriebssysteme und Linux-Distributionen stehen zur Auswahl?",
      a: "Sie können Ubuntu (20.04, 22.04, 24.04 LTS), Debian (11, 12), AlmaLinux (8, 9), Rocky Linux oder Windows Server per Mausklick installieren. Über das Kundenportal ist eine Neuinstallation (Re-Install) jederzeit kostenfrei möglich.",
    },
    {
      q: "Ist der Schweizer DDoS-Schutz in den Tarifen inbegriffen?",
      a: "Ja! Alle RedWORK VDS- und Cloud-Server befinden sich in unserem Schweizer Tier-IV Rechenzentrum Zürich hinter einem hardwarebasierten 1 Tbit/s DDoS-Schutzschild. SYN-Flood-, UDP- und HTTP/S-Angriffe werden ohne Latenzverlust gefiltert.",
    },
    {
      q: "Kann ich meinen Server später flexibel auf einen höheren Tarif upgraden?",
      a: "Selbstverständlich. Wenn Ihr Projekt oder Ihr Datenaufkommen wächst, können Sie CPU-Kerne, RAM und NVMe-Speicherplatz jederzeit direkt im Kundenportal ohne Datenverlust nach oben skalieren.",
    },
  ];

  return (
    <div className="space-y-16 sm:space-y-24">
      {/* ================================================== */}
      {/* 1. MEOHOST STYLE HERO (100% German)                */}
      {/* ================================================== */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-[#0E1424] via-[#090C16] to-[#07090F] border border-white/10 p-6 sm:p-10 lg:p-14 text-center">
        {/* Ambient Glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_0%,rgba(230,57,70,0.15),transparent)] pointer-events-none" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto space-y-4 sm:space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E63946]/40 bg-[#E63946]/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[#E63946] shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-[#E63946] animate-pulse" />
            <span>🇨🇭 PREMIUM VIRTUAL DEDICATED SERVER (VDS) • SCHWEIZER KVM POWER</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight">
            Grenzenlose Leistung. <span className="bg-gradient-to-r from-[#E63946] via-[#FF7A00] to-[#D4AF37] bg-clip-text text-transparent">Dedizierte Cloud-Power.</span>
          </h1>

          <p className="text-xs sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Intel Xeon Gold 6230R, Enterprise NVMe SSD und 10 Gbit/s Glasfaser-Anbindung. Garantierte KVM-Ressourcen und voller Root-Zugriff für anspruchsvolle Plattformen.
          </p>

          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 pt-2 flex-wrap">
            <a
              href="#vds-tarife"
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#E63946] to-[#FF7A00] hover:from-[#c5303d] hover:to-[#e56d00] text-white font-bold text-xs sm:text-sm shadow-lg shadow-red-600/25 transition transform hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              <span>VDS-Tarife ansehen</span>
              <ArrowRight size={15} />
            </a>

            <button
              type="button"
              onClick={() => {
                const el = document.getElementById("vds-lizenzen");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold text-xs sm:text-sm border border-white/10 transition inline-flex items-center gap-2 cursor-pointer"
            >
              <span>Software-Lizenzen & Features</span>
            </button>
          </div>

          {/* Hero Benefits Pills */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 pt-6 text-[11px] font-mono text-slate-300">
            <div className="bg-white/[0.03] p-2.5 rounded-xl border border-white/5 flex items-center justify-center gap-1.5">
              <Cpu size={13} className="text-[#FF7A00]" />
              <span>Xeon Gold 6230R</span>
            </div>
            <div className="bg-white/[0.03] p-2.5 rounded-xl border border-white/5 flex items-center justify-center gap-1.5">
              <Zap size={13} className="text-[#D4AF37]" />
              <span>DDR4 ECC RAM</span>
            </div>
            <div className="bg-white/[0.03] p-2.5 rounded-xl border border-white/5 flex items-center justify-center gap-1.5">
              <HardDrive size={13} className="text-emerald-400" />
              <span>NVMe RAID-10</span>
            </div>
            <div className="bg-white/[0.03] p-2.5 rounded-xl border border-white/5 flex items-center justify-center gap-1.5">
              <Globe size={13} className="text-cyan-400" />
              <span>10 Gbit/s Port</span>
            </div>
            <div className="bg-white/[0.03] p-2.5 rounded-xl border border-white/5 flex items-center justify-center gap-1.5">
              <ShieldCheck size={13} className="text-purple-400" />
              <span>1 Tbit/s DDoS</span>
            </div>
            <div className="bg-white/[0.03] p-2.5 rounded-xl border border-white/5 flex items-center justify-center gap-1.5">
              <Award size={13} className="text-[#E63946]" />
              <span>Tier-IV Zürich</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================== */}
      {/* 2. MEOHOST VDS PLANS TABLE (#vds-tarife)           */}
      {/* ================================================== */}
      <section id="vds-tarife" className="scroll-mt-24 space-y-8">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF7A00] mb-2">
            WÄHLEN SIE IHRE DEDIZIERTE POWER
          </p>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Das passende <em className="not-italic bg-gradient-to-r from-[#FF7A00] to-[#E63946] bg-clip-text text-transparent">VDS-Paket</em> für Ihr Projekt.
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-2">
            Garantierte KVM-Ressourcen, voller Root-Zugriff und Schweizer Rechenzentrum zum transparenten Festpreis in CHF.
          </p>

          {/* Billing Toggle (Monatlich / Jährlich) */}
          <div className="inline-flex items-center gap-2 bg-[#0F1420] p-1.5 rounded-2xl border border-white/10 mt-6 shadow-inner">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                billingCycle === "monthly"
                  ? "bg-[#E63946] text-white shadow-md"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Monatliche Zahlung
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle("yearly")}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                billingCycle === "yearly"
                  ? "bg-[#E63946] text-white shadow-md"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <span>Jährliche Zahlung</span>
              <span className="rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-black px-2 py-0.5 border border-emerald-500/30">
                10% Rabatt
              </span>
            </button>
          </div>
        </div>

        {/* -------------------------------------------------- */}
        {/* TABLE VIEW (Desktop / Tablet) & Card Hybrid        */}
        {/* -------------------------------------------------- */}
        <div className="rounded-2xl border border-white/10 bg-[#0B0E17] overflow-hidden shadow-2xl">
          {/* Table Header (Hidden on small screens) */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-6 py-3.5 bg-[#121826] border-b border-white/10 text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono">
            <div className="col-span-3">Tarif & Modell</div>
            <div className="col-span-2">CPU-Kerne</div>
            <div className="col-span-1">RAM</div>
            <div className="col-span-2">NVMe SSD</div>
            <div className="col-span-2">Anbindung & Traffic</div>
            <div className="col-span-2 text-right">Preis & Bestellen</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-white/5">
            {tableData.map((plan) => {
              const isHighlight = plan.isFeatured;

              return (
                <div
                  key={plan.id || plan.slug}
                  className={`p-4 sm:p-5 lg:px-6 lg:py-4 transition-all duration-200 hover:bg-white/[0.02] flex flex-col lg:grid lg:grid-cols-12 lg:items-center gap-4 ${
                    isHighlight
                      ? "bg-gradient-to-r from-[#E63946]/10 via-[#0B0E17] to-transparent border-l-4 border-l-[#E63946]"
                      : ""
                  }`}
                >
                  {/* Col 1: Tarif Name & Badge (3 Cols) */}
                  <div className="lg:col-span-3 flex items-center justify-between lg:justify-start gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm sm:text-base font-bold text-white">
                          {plan.displayName || plan.name}
                        </h3>
                        {isHighlight && (
                          <span className="rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider bg-gradient-to-r from-[#E63946] to-[#FF7A00] text-white shadow-sm">
                            ★ EMPFOHLEN
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-slate-400 block mt-0.5">
                        Garantierte KVM-Ressourcen
                      </span>
                    </div>

                    {/* Mobile Price Preview */}
                    <div className="lg:hidden text-right">
                      <del className="text-[11px] text-slate-500 font-mono block">
                        {plan.originalPrice.toFixed(2)} CHF
                      </del>
                      <div className="text-base font-bold font-mono text-[#D4AF37]">
                        {plan.discountedPrice.toFixed(2)} CHF
                        <span className="text-[10px] text-slate-400 font-normal"> / Mt.</span>
                      </div>
                    </div>
                  </div>

                  {/* Col 2: CPU (2 Cols) */}
                  <div className="lg:col-span-2 flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[#FF7A00] shrink-0">
                      <Cpu size={15} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white">
                        {plan.cores} vCPU Kerne
                      </div>
                      <div className="text-[10.5px] text-slate-400">
                        Intel Xeon Gold
                      </div>
                    </div>
                  </div>

                  {/* Col 3: RAM (1 Col) */}
                  <div className="lg:col-span-1 flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[#D4AF37] shrink-0">
                      <Zap size={15} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white">
                        {plan.ram}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        DDR4 ECC
                      </div>
                    </div>
                  </div>

                  {/* Col 4: NVMe SSD (2 Cols) */}
                  <div className="lg:col-span-2 flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-emerald-400 shrink-0">
                      <HardDrive size={15} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white">
                        {plan.disk}
                      </div>
                      <div className="text-[10.5px] text-slate-400">
                        NVMe PCIe 4.0
                      </div>
                    </div>
                  </div>

                  {/* Col 5: Anbindung (2 Cols) */}
                  <div className="lg:col-span-2 flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-cyan-400 shrink-0">
                      <Globe size={15} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white">
                        {plan.port} Port
                      </div>
                      <div className="text-[10.5px] text-slate-400">
                        {plan.traffic} Traffic
                      </div>
                    </div>
                  </div>

                  {/* Col 6: Preis & Bestellen (2 Cols) */}
                  <div className="lg:col-span-2 flex items-center justify-between lg:justify-end gap-3 pt-2 lg:pt-0 border-t border-white/5 lg:border-t-0">
                    <div className="hidden lg:block text-right">
                      <del className="text-[10.5px] text-slate-500 font-mono block">
                        {plan.originalPrice.toFixed(2)} CHF
                      </del>
                      <div className="text-sm font-bold font-mono text-white">
                        {plan.discountedPrice.toFixed(2)} CHF
                        <span className="text-[10px] text-slate-400 font-normal"> / Mt.</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => openOrderModal && openOrderModal(plan)}
                      className={`w-full lg:w-auto px-4 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-md cursor-pointer ${
                        isHighlight
                          ? "bg-gradient-to-r from-[#E63946] to-[#FF7A00] hover:from-[#c5303d] hover:to-[#e56d00] text-white shadow-red-600/25"
                          : "bg-white text-slate-900 hover:bg-slate-100"
                      }`}
                    >
                      <span>Jetzt Bestellen</span>
                      <ArrowRight size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* -------------------------------------------------- */}
        {/* ASSURANCES BAR (MeoHost Style, 100% German)        */}
        {/* -------------------------------------------------- */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 p-4 rounded-2xl bg-[#0F1420] border border-white/10 text-center text-xs font-medium text-slate-300">
          <div className="flex items-center justify-center gap-2 p-2">
            <Clock size={16} className="text-[#FF7A00]" />
            <span>Sofortige Bereitstellung</span>
          </div>
          <div className="flex items-center justify-center gap-2 p-2">
            <Award size={16} className="text-[#D4AF37]" />
            <span>Zürich Tier-IV Datacenter</span>
          </div>
          <div className="flex items-center justify-center gap-2 p-2">
            <Activity size={16} className="text-emerald-400" />
            <span>99.99% Verfügbarkeits-SLA</span>
          </div>
          <div className="flex items-center justify-center gap-2 p-2">
            <ShieldCheck size={16} className="text-cyan-400" />
            <span>1 Tbit/s DDoS-Schutz</span>
          </div>
          <div className="flex items-center justify-center gap-2 p-2 col-span-2 sm:col-span-1">
            <Headphones size={16} className="text-[#E63946]" />
            <span>24/7 Schweizer Support</span>
          </div>
        </div>
      </section>

      {/* ================================================== */}
      {/* 3. SOFTWARE & LIZENZEN (#vds-lizenzen)             */}
      {/* ================================================== */}
      <section id="vds-lizenzen" className="scroll-mt-24 space-y-8">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-[#D4AF37] mb-2">
            SOFTWARE & CONTROL PANELS
          </p>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Ihr Server ist bereit. <em className="not-italic bg-gradient-to-r from-[#D4AF37] to-[#FF7A00] bg-clip-text text-transparent">Inklusive modernster Software.</em>
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-2">
            Leistungsstarke Tools, Betriebssysteme und Control Panels für maximale Produktivität und Sicherheit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Card 1 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-[#101626] to-[#0A0D16] border border-white/10 space-y-4">
            <span className="text-[10px] font-black uppercase tracking-wider text-[#FF7A00] bg-[#FF7A00]/10 px-2.5 py-1 rounded-md border border-[#FF7A00]/20">
              CONTROL PANELS
            </span>
            <h3 className="text-base font-bold text-white">cPanel & Plesk Obsidian</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Verwalten Sie Ihre Websites, MariaDB-Datenbanken und E-Mail-Postfächer mit den weltweit führenden Verwaltungsoberflächen.
            </p>
            <ul className="space-y-1.5 text-xs text-slate-300 pt-2 border-t border-white/5">
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>cPanel / WHM Integration verfügbar</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>Plesk Web Pro & Host Edition</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>Automatische kostenlose SSL-Zertifikate</span>
              </li>
            </ul>
          </div>

          {/* Card 2 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-[#101626] to-[#0A0D16] border border-white/10 space-y-4">
            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-md border border-emerald-500/20">
              WEBSERVER PERFORMANCE
            </span>
            <h3 className="text-base font-bold text-white">LiteSpeed & Nginx / Redis</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Bis zu 84x schnellere Ladezeiten gegenüber Apache. LiteSpeed Enterprise und Redis Object Cache für minimale Latenzen.
            </p>
            <ul className="space-y-1.5 text-xs text-slate-300 pt-2 border-t border-white/5">
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>LiteSpeed Web Server Enterprise</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>LiteSpeed Cache (LSCache) & ESI</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>HTTP/3 & QUIC Protokollstandard</span>
              </li>
            </ul>
          </div>

          {/* Card 3 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-[#101626] to-[#0A0D16] border border-white/10 space-y-4">
            <span className="text-[10px] font-black uppercase tracking-wider text-cyan-400 bg-cyan-500/10 px-2.5 py-1 rounded-md border border-cyan-500/20">
              BETRIEBSSYSTEME & ISOLATION
            </span>
            <h3 className="text-base font-bold text-white">CloudLinux & Linux OS</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Sichere Mandanten-Trennung mit CageFS. Volle Betriebssystem-Freiheit mit Ubuntu, Debian, Rocky oder AlmaLinux.
            </p>
            <ul className="space-y-1.5 text-xs text-slate-300 pt-2 border-t border-white/5">
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>CloudLinux OS Shared Pro</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>Ubuntu 22.04 & 24.04 LTS Ready</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>AlmaLinux 9, Rocky & Debian 12</span>
              </li>
            </ul>
          </div>

          {/* Card 4 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-[#101626] to-[#0A0D16] border border-white/10 space-y-4">
            <span className="text-[10px] font-black uppercase tracking-wider text-purple-400 bg-purple-500/10 px-2.5 py-1 rounded-md border border-purple-500/20">
              ERWEITERTE SICHERHEIT
            </span>
            <h3 className="text-base font-bold text-white">Imunify360 & DDoS Shield</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              KI-gestützte Web Application Firewall (WAF), Echtzeit-Malware-Scanning und automatische Schutzfilterung.
            </p>
            <ul className="space-y-1.5 text-xs text-slate-300 pt-2 border-t border-white/5">
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>Imunify360 KI-Sicherheits-Firewall</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>1 Tbit/s Hardware DDoS-Filter Zürich</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-emerald-400" />
                <span>Proaktiver Schutz vor Brute-Force</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ================================================== */}
      {/* 4. SCHWEIZER INFRASTRUKTUR & FULL ROOT             */}
      {/* ================================================== */}
      <section className="space-y-8">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-[#E63946] mb-2">
            INFRASTRUKTUR & SICHERHEIT
          </p>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Kompromisslose Infrastruktur. <span className="bg-gradient-to-r from-[#E63946] to-[#FF7A00] bg-clip-text text-transparent">Volle Root-Kontrolle.</span>
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-2">
            Zürich Tier-IV Standards mit N+1 Redundanz, Diesel-Notstrom und 100% Schweizer Datenschutz (DSG).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="bg-[#0F1420] p-6 rounded-2xl border border-white/10 space-y-3">
            <div className="h-10 w-10 rounded-xl bg-[#E63946]/10 border border-[#E63946]/20 flex items-center justify-center text-[#E63946]">
              <Layers size={20} />
            </div>
            <h4 className="text-base font-bold text-white">Garantierte Ressourcen</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Dank KVM-Vollvirtualisierung sind CPU-Kerne, ECC-RAM und NVMe-Speicher garantiert für Sie reserviert. Null Overselling.
            </p>
          </div>

          <div className="bg-[#0F1420] p-6 rounded-2xl border border-white/10 space-y-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <ShieldCheck size={20} />
            </div>
            <h4 className="text-base font-bold text-white">Redundante Verfügbarkeit</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Zürich Tier-IV Rechenzentrum mit N+1 Generatoren, redundanter USV und Carrier-neutraler 10 Gbit/s Glasfaser-Anbindung.
            </p>
          </div>

          <div className="bg-[#0F1420] p-6 rounded-2xl border border-white/10 space-y-3">
            <div className="h-10 w-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]">
              <Zap size={20} />
            </div>
            <h4 className="text-base font-bold text-white">Nahtlose Skalierung</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Wächst Ihr Projekt, erweitern Sie CPU, RAM und SSD-Speicherplatz jederzeit direkt über das Kundenportal ohne Datenverlust.
            </p>
          </div>

          <div className="bg-[#0F1420] p-6 rounded-2xl border border-white/10 space-y-3">
            <div className="h-10 w-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
              <Headphones size={20} />
            </div>
            <h4 className="text-base font-bold text-white">24/7 Schweizer Engineering</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Unsere erfahrenen System-Engineers unterstützen Sie jederzeit bei Setup, Migration und individueller Server-Konfiguration.
            </p>
          </div>
        </div>
      </section>

      {/* ================================================== */}
      {/* 5. VDS FAQ (100% German)                            */}
      {/* ================================================== */}
      <section className="space-y-6 max-w-3xl mx-auto">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF7A00]">
            HÄUFIG GESTELLTE FRAGEN
          </p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">
            Fragen & Antworten zu VDS.
          </h2>
        </div>

        <div className="space-y-2.5">
          {faqs.map((faq, idx) => {
            const isOpen = activeFaq === idx;
            return (
              <div
                key={idx}
                className="rounded-xl border border-white/10 bg-[#0F1420] overflow-hidden transition"
              >
                <button
                  type="button"
                  onClick={() => setActiveFaq(isOpen ? null : idx)}
                  className="w-full p-4 text-left flex items-center justify-between gap-3 font-semibold text-xs sm:text-sm text-white hover:text-[#FF7A00] transition cursor-pointer"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    size={16}
                    className={`shrink-0 transition-transform duration-200 ${
                      isOpen ? "rotate-180 text-[#FF7A00]" : "text-slate-400"
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 text-xs text-slate-300 leading-relaxed border-t border-white/5 pt-2.5 font-normal">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
